import type { CSSProperties } from 'react';
import { bg0 } from '@osmosis/tailwind-ui/styles/background';
import { borderColorBlue600 } from '@osmosis/tailwind-ui/styles/border';
import { text4 } from '@osmosis/tailwind-ui/styles/text';

/**
 * Radial-gradient border around the dashboard hero input in AI Chat mode
 * (Figma node 872-735). Uses the two-background-layers technique since
 * `border-image` ignores `border-radius`.
 */
export const aiChatGradientBorderStyle: CSSProperties = {
  border: '1px solid transparent',
  backgroundImage:
    'linear-gradient(#fff, #fff), radial-gradient(100% 100% at 0% 49.09%, #5A5FE2 0%, #9254CD 28.37%, #DB47B2 50.96%, #FF5C8E 75.96%, #FF9562 100%)',
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box',
};

/**
 * Full placeholder shared by every site search input.
 */
export const searchPlaceholderMessage = {
  id: 'header.search.placeholder_text',
  defaultMessage: 'Search for videos and notes',
} as const;

/**
 * Shorter placeholder used where the header search is too narrow for the full copy.
 */
export const compactSearchPlaceholderMessage = {
  id: 'dashboard.welcomeHeader.searchBtn',
  defaultMessage: 'Search',
} as const;

/**
 * Placeholder shown when the dashboard hero input is in AI Chat mode.
 */
export const aiChatPlaceholderMessage = {
  id: 'dashboard.welcomeHeader.aiChatPlaceholder',
  defaultMessage: "Ask a medical question or tell me what you're studying",
} as const;

type SearchContainerClassOptions = {
  heightClass?: string;
  isSearchResultsOpen?: boolean;
  zIndexClass?: string;
  borderClass?: string;
};

/**
 * Builds the canonical search container classes.
 *
 * Every site search input shares this, so the bar looks the same on the
 * dashboard, in the header (logged in and logged out) and in public heroes.
 * Only height and stacking order vary per mount.
 */
export const getSearchContainerClasses = ({
  heightClass = 'h-[55px]',
  isSearchResultsOpen = false,
  zIndexClass = 'z-[100]',
  borderClass = borderColorBlue600,
}: SearchContainerClassOptions = {}) => `min-w-0 ${bg0} ${heightClass} relative ${zIndexClass} w-full
  ${isSearchResultsOpen ? 'max-w-none' : ''}
  border ${borderClass}
  rounded-lg
  focus-within:ring-2 focus-within:ring-blue-400 focus-within:border-blue-400
  [&_input]:bg-transparent
  [&_input]:dark:bg-transparent
  [&_input]:border-none
  [&_input]:outline-none
  [&_input]:py-3
  [&_input]:pl-[38px]
  [&_input]:pr-[5px]
  [&_input]:!${text4}
  [&_input]:placeholder:${text4}`;
/**
 * Checks whether the current route should use the wide header search layout.
 *
 * This controls the header search *width* only — the styling above is applied
 * unconditionally so every search input matches.
 */
export const shouldUseWideHeaderSearch = (pathname?: string) =>
  !!(
    pathname === '/answers' ||
    pathname?.startsWith('/answers/') ||
    pathname?.startsWith('/learn/')
  );
